import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, Video, CheckCircle2, ChevronRight, Globe } from "lucide-react";

const API = "/api";

const TEXTS = {
  en: {
    welcome: "Hello! Thank you for your interest in our English lessons.",
    subtitle: "We offer trial classes via Zoom Meeting. Please choose your available time below.",
    chooseTutor: "Choose Your Tutor",
    chooseDate: "Choose Your Date",
    chooseTime: "Choose Available Time",
    yourInfo: "Your Information",
    fullName: "Full Name",
    email: "Email Address",
    whatsapp: "WhatsApp Number",
    englishLevel: "English Level",
    levels: ["Beginner", "Elementary", "Pre-Intermediate", "Intermediate", "Upper-Intermediate", "Advanced"],
    bookNow: "Book My Trial Class",
    booking: "Booking...",
    turkeyTime: "Turkey Time (Istanbul)",
    confirmed: "Your Trial Class is Confirmed!",
    classDetails: "Class Details",
    tutor: "Tutor",
    date: "Date",
    time: "Time",
    timezone: "Timezone",
    zoomLink: "Zoom Meeting Link",
    joinClass: "Join Zoom Class",
    reminder: "Please click the Zoom link 5 minutes before your scheduled time.",
    checkEmail: "A confirmation email has been sent to your inbox.",
    paymentTitle: "Ready to Enroll?",
    paymentBtn: "View Payment Instructions",
    countdown: "Your trial class starts in",
    selectTutor: "Please select a tutor",
    selectTime: "Please select a time slot",
    fillAll: "Please fill in all fields",
    unavailable: "This time slot is no longer available",
    langSwitch: "Turkce",
  },
  tr: {
    welcome: "Merhaba! Ingilizce derslerimize gosterdiginiz ilgi icin tesekkur ederiz.",
    subtitle: "Zoom uzerinden deneme dersleri sunuyoruz. Lutfen asagidan uygun zamaninizi secin.",
    chooseTutor: "Ogretmeninizi Secin",
    chooseDate: "Tarihinizi Secin",
    chooseTime: "Uygun Saati Secin",
    yourInfo: "Bilgileriniz",
    fullName: "Ad Soyad",
    email: "E-posta Adresi",
    whatsapp: "WhatsApp Numarasi",
    englishLevel: "Ingilizce Seviyesi",
    levels: ["Baslangic", "Temel", "Orta Alti", "Orta", "Orta Ustu", "Ileri"],
    bookNow: "Deneme Dersimi Ayirt",
    booking: "Ayirtiliyor...",
    turkeyTime: "Turkiye Saati (Istanbul)",
    confirmed: "Deneme Dersiniz Onaylandi!",
    classDetails: "Ders Detaylari",
    tutor: "Ogretmen",
    date: "Tarih",
    time: "Saat",
    timezone: "Saat Dilimi",
    zoomLink: "Zoom Toplanti Linki",
    joinClass: "Zoom Dersine Katil",
    reminder: "Lutfen ders saatinden 5 dakika once Zoom linkine tiklayin.",
    checkEmail: "Onay e-postasi gonderildi.",
    paymentTitle: "Kayit Olmaya Hazir Misiniz?",
    paymentBtn: "Odeme Talimatlari",
    countdown: "Deneme dersinize kalan sure",
    selectTutor: "Lutfen bir ogretmen secin",
    selectTime: "Lutfen bir saat secin",
    fillAll: "Lutfen tum alanlari doldurun",
    unavailable: "Bu saat artik musait degil",
    langSwitch: "English",
  }
};

const LEVEL_MAP = {
  "Beginner": "Beginner", "Elementary": "Elementary", "Pre-Intermediate": "Pre-Intermediate",
  "Intermediate": "Intermediate", "Upper-Intermediate": "Upper-Intermediate", "Advanced": "Advanced",
  "Baslangic": "Beginner", "Temel": "Elementary", "Orta Alti": "Pre-Intermediate",
  "Orta": "Intermediate", "Orta Ustu": "Upper-Intermediate", "Ileri": "Advanced"
};

function CountdownTimer({ targetDate, targetTime, label }) {
  const [timeLeft, setTimeLeft] = useState("");
  useEffect(() => {
    const interval = setInterval(() => {
      const target = new Date(`${targetDate}T${targetTime}:00+03:00`);
      const now = new Date();
      const diff = target - now;
      if (diff <= 0) { setTimeLeft("Class is starting now!"); clearInterval(interval); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${d > 0 ? d + "d " : ""}${h}h ${m}m ${s}s`);
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate, targetTime]);
  return (
    <div className="text-center py-4 px-6 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border border-teal-200" data-testid="countdown-timer">
      <p className="text-sm text-teal-600 font-medium">{label}</p>
      <p className="text-2xl font-bold text-teal-800 mt-1 font-mono">{timeLeft}</p>
    </div>
  );
}

export default function TrialBooking() {
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState(searchParams.get("lang") || "en");
  const t = TEXTS[lang];

  const [tutors, setTutors] = useState({});
  const [step, setStep] = useState(1);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", level: "", referral_code: "" });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [trialLimit, setTrialLimit] = useState(null); // { max, used, remaining, can_book }
  const [referralInfo, setReferralInfo] = useState(null); // { valid, referrer_name, reward_credits }

  // Auto-fill referral code from ?ref= URL param
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
  }, [searchParams]);

  // Validate referral code as user types
  useEffect(() => {
    const code = (form.referral_code || "").trim();
    if (!code || code.length < 5) {
      setReferralInfo(null);
      return;
    }
    const t = setTimeout(() => {
      axios
        .get(`${API}/referrals/validate/${encodeURIComponent(code)}`)
        .then((r) => setReferralInfo(r.data))
        .catch(() => setReferralInfo({ valid: false }));
    }, 400);
    return () => clearTimeout(t);
  }, [form.referral_code]);

  useEffect(() => {
    axios.get(`${API}/trial/tutors`).then(r => setTutors(r.data.tutors)).catch(() => {});
  }, []);

  // Check trial limit when email is filled in
  useEffect(() => {
    if (form.email && form.email.includes("@")) {
      const t = setTimeout(() => {
        axios
          .get(`${API}/trial/trial-limit`, { params: { email: form.email, student_name: form.name } })
          .then((r) => setTrialLimit(r.data))
          .catch(() => setTrialLimit(null));
      }, 500);
      return () => clearTimeout(t);
    }
    setTrialLimit(null);
  }, [form.email, form.name]);

  useEffect(() => {
    if (selectedTutor && selectedDate) {
      axios.get(`${API}/trial/availability?date=${selectedDate}&tutor=${selectedTutor}`)
        .then(r => { setAvailability(r.data.slots); setSelectedTime(null); })
        .catch(() => setAvailability([]));
    }
  }, [selectedTutor, selectedDate]);

  // Generate next 14 days
  const dates = [];
  for (let i = 1; i <= 14; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    dates.push(d.toISOString().split("T")[0]);
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const formatTime12 = (t24) => {
    const [h, m] = t24.split(":");
    const hour = parseInt(h);
    return `${hour > 12 ? hour - 12 : hour}:${m} ${hour >= 12 ? "PM" : "AM"}`;
  };

  const handleBook = async () => {
    if (!selectedTutor) { toast.error(t.selectTutor); return; }
    if (!selectedTime) { toast.error(t.selectTime); return; }
    if (!form.name || !form.email || !form.whatsapp || !form.level) { toast.error(t.fillAll); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/trial/book`, {
        student_name: form.name,
        email: form.email,
        whatsapp: form.whatsapp,
        english_level: LEVEL_MAP[form.level] || form.level,
        tutor: selectedTutor,
        date: selectedDate,
        time: selectedTime,
        language: lang,
        referral_code: form.referral_code || ""
      });
      setConfirmation(res.data);
      toast.success(lang === "tr" ? "Basariyla ayirtildi!" : "Booking confirmed!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setLoading(false);
    }
  };

  // Confirmation screen
  if (confirmation) {
    const tutor = tutors[selectedTutor];
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg shadow-xl border-0 overflow-hidden" data-testid="booking-confirmation">
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-center">
            <CheckCircle2 className="w-14 h-14 text-white mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white">{t.confirmed}</h1>
          </div>
          <CardContent className="p-6 space-y-5">
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 space-y-3">
              <h3 className="font-semibold text-teal-800">{t.classDetails}</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-slate-500">{t.tutor}:</span><span className="font-semibold">{confirmation.tutor_name}</span>
                <span className="text-slate-500">{t.date}:</span><span className="font-semibold">{confirmation.date}</span>
                <span className="text-slate-500">{t.time}:</span><span className="font-semibold">{formatTime12(confirmation.time)}</span>
                <span className="text-slate-500">{t.timezone}:</span><span className="font-semibold">{t.turkeyTime}</span>
              </div>
            </div>

            <CountdownTimer targetDate={confirmation.date} targetTime={confirmation.time} label={t.countdown} />

            <div className="text-center space-y-3">
              <p className="text-sm text-slate-500">{t.zoomLink}</p>
              <a href={confirmation.zoom_link} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors"
                data-testid="join-zoom-btn">
                <Video className="w-5 h-5" /> {t.joinClass}
              </a>
            </div>

            <p className="text-amber-600 text-sm text-center font-medium">{t.reminder}</p>
            <p className="text-slate-400 text-xs text-center">{t.checkEmail}</p>

            <div className="pt-4 border-t">
              <a href="/payment" className="block w-full text-center bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold py-3 rounded-xl transition-colors"
                data-testid="payment-btn">
                {t.paymentBtn} <ChevronRight className="w-4 h-4 inline ml-1" />
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Booking form
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Language toggle */}
        <div className="flex justify-end">
          <button onClick={() => setLang(lang === "en" ? "tr" : "en")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-teal-200 text-teal-700 hover:bg-teal-50 text-sm font-medium transition-colors"
            data-testid="lang-toggle">
            <Globe className="w-4 h-4" /> {t.langSwitch}
          </button>
        </div>

        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-teal-800">LanguaConnect English Class</h1>
          <p className="text-slate-600 text-lg">{t.welcome}</p>
          <p className="text-slate-500">{t.subtitle}</p>
        </div>

        {/* Step 1: Choose Tutor */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="bg-teal-600 px-5 py-3"><h2 className="text-white font-semibold flex items-center gap-2"><span className="bg-white/20 w-7 h-7 rounded-full flex items-center justify-center text-sm">1</span>{t.chooseTutor}</h2></div>
          <CardContent className="p-5">
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(tutors).map(([key, tutor]) => (
                <button key={key} onClick={() => { setSelectedTutor(key); if (step < 2) setStep(2); }}
                  className={`relative p-4 rounded-xl border-2 transition-all text-center ${selectedTutor === key ? "border-teal-500 bg-teal-50 shadow-md" : "border-slate-200 hover:border-teal-300"}`}
                  data-testid={`tutor-${key}`}>
                  <img src={tutor.photo} alt={tutor.name} className="w-24 h-24 rounded-full mx-auto mb-3 object-cover border-4 border-white shadow-md" />
                  <p className="font-semibold text-slate-800">{tutor.name}</p>
                  {selectedTutor === key && <div className="absolute top-2 right-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center"><CheckCircle2 className="w-4 h-4 text-white" /></div>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Choose Date */}
        {step >= 2 && (
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="bg-teal-600 px-5 py-3"><h2 className="text-white font-semibold flex items-center gap-2"><Calendar className="w-5 h-5" /><span className="bg-white/20 w-7 h-7 rounded-full flex items-center justify-center text-sm">2</span>{t.chooseDate}</h2></div>
            <CardContent className="p-5">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {dates.map(d => (
                  <button key={d} onClick={() => { setSelectedDate(d); if (step < 3) setStep(3); }}
                    className={`p-2.5 rounded-lg text-sm font-medium transition-all ${selectedDate === d ? "bg-teal-600 text-white shadow-md" : "bg-slate-50 text-slate-700 hover:bg-teal-50 border border-slate-200"}`}
                    data-testid={`date-${d}`}>
                    {formatDate(d)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3 text-center">{t.turkeyTime}</p>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Choose Time */}
        {step >= 3 && selectedDate && (
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="bg-teal-600 px-5 py-3"><h2 className="text-white font-semibold flex items-center gap-2"><Clock className="w-5 h-5" /><span className="bg-white/20 w-7 h-7 rounded-full flex items-center justify-center text-sm">3</span>{t.chooseTime}</h2></div>
            <CardContent className="p-5">
              <div className="grid grid-cols-3 gap-3">
                {availability.map(slot => (
                  <button key={slot.time} onClick={() => { if (slot.available) { setSelectedTime(slot.time); if (step < 4) setStep(4); } }}
                    disabled={!slot.available}
                    className={`p-3 rounded-xl text-center transition-all ${
                      !slot.available ? "bg-slate-100 text-slate-400 cursor-not-allowed line-through" :
                      selectedTime === slot.time ? "bg-teal-600 text-white shadow-md" : "bg-emerald-50 text-teal-700 hover:bg-teal-100 border border-teal-200"
                    }`}
                    data-testid={`time-${slot.time}`}>
                    <span className="font-semibold">{formatTime12(slot.time)}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Student Info */}
        {step >= 4 && (
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="bg-teal-600 px-5 py-3"><h2 className="text-white font-semibold flex items-center gap-2"><span className="bg-white/20 w-7 h-7 rounded-full flex items-center justify-center text-sm">4</span>{t.yourInfo}</h2></div>
            <CardContent className="p-5 space-y-4">
              <div className="space-y-2">
                <Label>{t.fullName}</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Ahmet Yilmaz" data-testid="input-name" />
              </div>
              <div className="space-y-2">
                <Label>{t.email}</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="your@email.com" data-testid="input-email" />
                {trialLimit && (
                  trialLimit.can_book ? (
                    <p className="text-xs text-emerald-600" data-testid="trial-limit-info">
                      ✓ {trialLimit.remaining} of {trialLimit.max} free trials remaining for this email
                    </p>
                  ) : (
                    <div className="mt-2 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="trial-limit-exceeded">
                      <p className="font-semibold">⛔ Free trial limit reached</p>
                      <p className="text-xs mt-1">
                        You've already used your {trialLimit.max} free trial classes. Please <a href="/subscribe" className="underline font-semibold">subscribe to a package</a> to keep learning with us.
                      </p>
                    </div>
                  )
                )}
              </div>
              <div className="space-y-2">
                <Label>{t.whatsapp}</Label>
                <Input value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} placeholder="+90 5XX XXX XXXX" data-testid="input-whatsapp" />
              </div>
              <div className="space-y-2">
                <Label>{t.englishLevel}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {t.levels.map(level => (
                    <button key={level} onClick={() => setForm({...form, level})}
                      className={`p-2 rounded-lg text-sm font-medium transition-all ${form.level === level ? "bg-teal-600 text-white" : "bg-slate-50 border border-slate-200 hover:bg-teal-50"}`}
                      data-testid={`level-${level}`}>
                      {level}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  Referral Code <span className="text-xs text-slate-400">(optional)</span>
                </Label>
                <Input
                  value={form.referral_code}
                  onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                  placeholder="LANG-XXXXX"
                  className="font-mono"
                  data-testid="input-referral-code"
                />
                {referralInfo && (
                  referralInfo.valid ? (
                    <p className="text-xs text-emerald-600" data-testid="referral-valid">
                      ✓ Valid code from <strong>{referralInfo.referrer_name || "a student"}</strong>
                    </p>
                  ) : (
                    <p className="text-xs text-rose-600" data-testid="referral-invalid">
                      ⚠️ Code not recognized — make sure it's correct.
                    </p>
                  )
                )}
              </div>
              <Button onClick={handleBook} disabled={loading || (trialLimit && !trialLimit.can_book)}
                className="w-full h-12 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-bold text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="book-trial-btn">
                {loading ? t.booking : (trialLimit && !trialLimit.can_book) ? "Trial Limit Reached" : t.bookNow}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
